import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { EndPoints } from "../../Constants/endpoints";
import { useToast } from "../../Services/ToastContext";
import useAddPrescriptionToPatient from "../../api/Prescription/useAddPrescriptionToPatient";
import { RootState } from "../../redux/store";
import { clearSelectedPrescription } from "../../redux/reducers/selectedPrescriptionSlice";
import { handleExportPDF } from "./utils";
import {
  cleanFormData,
  mapTemplateToDrugs,
  mergeDrugs,
} from "./hooks/usePrescriptionSubmit";
import { usePostData } from "../../hooks";
import { PrescriptionBaseForm } from "./components";

const AddPrescriptionToPatientByTemplate: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { PatientId } = useParams();

  const location = useLocation();

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };
  const [addedPrescriptionId, setAddedPrescriptionId] = useState<number>();
  const [localInitialData, setLocalInitialData] = useState<any>();

  const selectedTemplate = useSelector(
    (state: RootState) => state.selectedPrescription.selectedTemplate,
  );

  const { mutate, isPending } = useAddPrescriptionToPatient();
  const { mutate: printMutate } = usePostData(
    `${EndPoints.PrintPrescription}/${addedPrescriptionId}`,
  );

  useEffect(() => {
    if (selectedTemplate) {
      const { formDrugs } = mapTemplateToDrugs(selectedTemplate);
      setLocalInitialData({
        Case: selectedTemplate.Case || "",
        Drugs: formDrugs,
      });
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (addedPrescriptionId) {
      printMutate(
        {},
        {
          onSuccess: (data) => {
            handleExportPDF(data);
            setAddedPrescriptionId(undefined);
            handleBack();
          },
        },
      );
    }
  }, [addedPrescriptionId, printMutate, navigate]);

  const handleDrugsChange = (newDrugs: any[]) => {
    setLocalInitialData((prev: any) => ({
      ...prev,
      Drugs: mergeDrugs(prev?.Drugs || [], newDrugs),
    }));
  };

  const handleSubmit = (data: any) => {
    const cleanedData = cleanFormData(data);

    mutate(
      { patientId: Number(PatientId), data: cleanedData },
      {
        onSuccess: (response) => {
          showToast(t("prescription_added_successfully"), "success");
          setAddedPrescriptionId(response?.Data?.Id);
          dispatch(clearSelectedPrescription()); 
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetPrescriptions],
          });
          queryClient.refetchQueries({
            queryKey: [`${EndPoints.GetPrescriptionToPatient}/${PatientId}`],
          });
        },
        onError: (error: any) => {
          showToast(error.message || t("error_adding_prescription"), "error");
        },
      },
    );
  };

  return (
    <PrescriptionBaseForm
      key={localInitialData?.Drugs?.length}
      patientId={PatientId}
      initialData={localInitialData}
      onSubmit={handleSubmit}
      onDrugsChange={handleDrugsChange}
      config={{
        title: t("add_prescription"),
        submitButtonText: t("save_and_print"),
        caseLabel: t("case_name"),
        showPatientInfo: true,
        showPrint: true,
      }}
      isLoading={isPending}
    />
  );
};

export default AddPrescriptionToPatientByTemplate;
